import { Palette } from '@mui/material';
import type { ThemeOptions } from '@mui/material/styles';

export const buildThemeTypography = (palette: Palette): ThemeOptions['typography'] => ({
  fontFamily: 'Neue Frutiger World',
  fontWeightLight: 300,
  fontWeightRegular: 500,
  fontWeightMedium: 600,
  fontWeightBold: 700,
  h1: {
    fontSize: '2.125rem',
    lineHeight: '2rem',
    color: palette.grey[600],
  },
  h2: {
    fontSize: '1.875rem',
    lineHeight: '1.875rem',
    color: palette.grey[600],
  },
  h3: {
    fontSize: '1.6875rem',
    lineHeight: '1.75rem',
    color: palette.grey[600],
  },
  h4: {
    fontSize: '1.375rem',
    lineHeight: '1.625rem',
    color: palette.grey[600],
  },
  h5: {
    fontSize: '1.125rem',
    lineHeight: '1.375rem',
    color: palette.grey[600],
  },
  body1: {
    fontSize: '0.875rem',
    lineHeight: '1.1875rem',
    fontWeight: 500,
    color: palette.grey[500],
  },
  body2: {
    fontSize: '0.75rem',
    lineHeight: '1.1875rem',
    color: palette.grey[500],
  },
  subtitle1: {
    fontSize: '1rem',
    lineHeight: '1.3125rem',
    color: palette.grey[500],
  },
  caption: {
    fontSize: '1rem',
    lineHeight: '1.3125rem',
    color: palette.grey[500],
    fontStyle: 'italic',
  },
});

export const buildThemeComponents = (palette: Palette): ThemeOptions['components'] => ({
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        fontFamily: 'Neue Frutiger World',
      },
      '*': {
        scrollbarColor: `${palette.scrollbar.thumb} ${palette.scrollbar.track}`,
      },
      '*::-webkit-scrollbar': {
        width: '6px',
        height: '6px',
      },
      '*::-webkit-scrollbar-track': {
        background: palette.scrollbar.track,
      },
      '*::-webkit-scrollbar-thumb': {
        background: palette.scrollbar.thumb,
        borderRadius: '20px',
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        fontFamily: 'Neue Frutiger World',
      },
    },
  },
  MuiTypography: {
    styleOverrides: {
      root: {
        fontFamily: 'Neue Frutiger World',
      },
    },
  },

  MuiButtonBase: {
    defaultProps: {
      disableRipple: true,
    },
  },
  MuiSelect: {
    styleOverrides: {
      root: {
        fontFamily: 'Neue Frutiger World',
        padding: '0 !important',
        '.MuiSelect-select': {
          paddingRight: '2rem',
          paddingTop: '0.4rem',
          paddingBottom: '0.4rem',
        },
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: ({ ownerState }: any) => {
        return {
          boxShadow: 'none !important',
          fontFamily: 'Neue Frutiger World',
          borderRadius: ownerState.size === 'small' ? '0.25rem' : '0.5rem',
          fontSize:
            ownerState.size === 'small'
              ? '0.75rem'
              : ownerState.size === 'medium'
                ? '0.75rem'
                : '1rem',
          fontWeight: ownerState.size === 'small' ? 400 : 500,
          padding: ownerState.size === 'small' ? '0.25rem 0.5rem' : '0.5rem 1rem',
          lineHeight: ownerState.size === 'small' ? '110%' : '160%',
          textTransform: 'none',
          gap: '0.375rem',
          minWidth: 0,

          '& .MuiButton-startIcon': {
            width: 'auto',
            marginLeft: 0,
            marginRight: 0,
          },
          '& .MuiInputAdornment-positionStart': {
            width: 'auto',
            marginLeft: 0,
            marginRight: 0,
          },
        };
      },
      outlined: ({ ownerState }: any) => {
        let color;
        switch (ownerState.color) {
          case 'primary':
            color = palette.accent;
            break;
          case 'secondary':
            color = palette.secondary;
            break;
          case 'error':
            color = palette.error;
            break;
          case 'success':
            color = palette.success;
            break;
          case 'warning':
            color = palette.warning;
            break;
          case 'info':
            color = palette.info;
            break;
          default:
            color = palette.primary;
        }

        const extraStyles = {
          color: color.main,
          borderColor: color.main,
          backgroundColor: 'rgba(255,255,255,0.1)',

          '&:hover': {
            color: color.main,
            borderColor: color.main,
            backgroundColor: color.light,
            opacity: 0.8,
          },
          '&:active': {
            color: color.main,
            borderColor: color.main,
            backgroundColor: color.light,
            opacity: 0.6,
          },
        };

        return {
          '&:disabled': {
            padding: '0.4375rem 1rem',
            borderWidth: 1,
            borderStyle: 'solid',
            backgroundColor: palette.grey[200],
            borderColor: palette.grey[300],
            color: palette.grey[400],
          },
          ...extraStyles,
        };
      },
      contained: ({ ownerState }: any) => {
        let color;
        switch (ownerState.color) {
          case 'primary':
            color = palette.accent;
            break;
          case 'secondary':
            color = palette.secondary;
            break;
          case 'error':
            color = palette.error;
            break;
          case 'success':
            color = palette.success;
            break;
          case 'warning':
            color = palette.warning;
            break;
          case 'info':
            color = palette.info;
            break;
          default:
            color = palette.primary;
        }

        const extraStyles = {
          color: color.light,
          backgroundColor: color.main,

          '&:hover': {
            color: color.light,
            backgroundColor: color.main,
            opacity: 0.8,
          },
          '&:active': {
            color: color.light,
            backgroundColor: color.main,
            opacity: 0.6,
          },
        };

        return {
          borderWidth: 1,
          borderStyle: 'solid',

          '&:disabled': {
            padding: '0.4375rem 1rem',
            borderWidth: 1,
            borderStyle: 'solid',
            backgroundColor: palette.grey[200],
            borderColor: palette.grey[300],
            color: palette.grey[400],
          },

          ...extraStyles,
        };
      },
      containedSecondary: () => {
        const extraStyles = {
          color: palette.grey[800],
          borderColor: palette.grey[100],
          backgroundColor: palette.grey[100],

          '&:hover': {
            color: palette.grey[700],
            borderColor: palette.grey[50],
            backgroundColor: palette.grey[50],
          },
          '&:active': {
            color: palette.grey[600],
            borderColor: palette.grey[50],
            backgroundColor: palette.grey[50],
          },
        };

        return {
          ...extraStyles,
        };
      },
      textSecondary: () => {
        const extraStyles = {
          padding: '0.25rem 0.5rem',
          color: palette.grey[800],

          '&:hover': {
            color: palette.grey[800],
            borderColor: palette.grey[100],
            backgroundColor: palette.grey[100],
          },
          '&:active': {
            color: palette.grey[800],
            borderColor: palette.grey[200],
            backgroundColor: palette.grey[200],
          },
        };

        return {
          ...extraStyles,
        };
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: '10px',
        padding: '20px',
        fontFamily: 'Neue Frutiger World',
      },
    },
    variants: [
      {
        props: { variant: 'inner-gray' },
        style: {
          borderRadius: '6px',
          padding: '10px',
          backgroundColor: palette.grey[200],
        },
      },
      {
        props: { variant: 'inner' },
        style: {
          borderRadius: '8px',
          padding: '10px',
        },
      },
    ],
  },
  MuiInputBase: {
    styleOverrides: {
      root: {
        borderRadius: '0.375rem',
        fontFamily: 'Neue Frutiger World',
        padding: '0.75rem 0.5rem !important',
        border: '1px solid rgba(0, 0, 0, 0.15)',

        '&.Mui-error': {
          border: `1px solid ${palette.error.main} !important`,
        },

        '& input': {
          fontWeight: 500,
          fontSize: '0.75rem',
          lineHeight: '0.75rem',
          height: '1rem',
          color: palette.grey[800],
          padding: '0 !important',

          '::placeholder': {
            color: palette.grey[400],
          },

          '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus': {
            WebkitBoxShadow: '0 0 0 62.5rem white inset',
            WebkitTextFillColor: palette.grey[800],
          },
        },

        '& fieldset': {
          border: 'none',
        },

        '&:focus': {
          background: 'transparent',
        },

        '&.Mui-disabled': {
          backgroundColor: '#fafafa',
          border: `1px solid ${palette.grey[200]}`,
          cursor: 'not-allowed',
          '& input': {
            WebkitTextFillColor: palette.grey[400],
            cursor: 'not-allowed',
          },
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: ({ ownerState }: any) => ({
        borderRadius: '0.25rem',
        margin: '0 !important',
        padding:
          ownerState.size === 'small' ? '0.12rem 0.25rem !important' : '0.25rem 0.5rem !important',

        '.MuiChip-label': {
          fontFamily: 'Neue Frutiger World',
          fontSize: '0.75rem',
          fontStyle: 'normal',
          fontWeight: 400,
          lineHeight: '160%',
          padding: '0 !important',
        },
        svg: {
          marginRight: '0.25rem !important',
        },
      }),
      outlinedAccent: {
        color: palette.grey[800],
        backgroundColor: 'white',
      },

      outlinedPrimary: {
        color: palette.accent.main,
        borderColor: palette.accent.main,
        backgroundColor: palette.accent.light,

        '.MuiChip-deleteIcon': {
          marginLeft: '0.25rem',
          color: palette.accent.main,
        },
      },

      outlinedSecondary: {
        color: palette.grey[600],
        borderColor: palette.grey[300],
        backgroundColor: '#FEFEFE',

        '.MuiChip-deleteIcon': {
          marginLeft: '0.5rem',
          color: palette.grey[800],
        },
      },
    },
  },
  MuiAutocomplete: {
    styleOverrides: {
      root: {
        '.MuiInputBase-root': {
          fontFamily: 'Neue Frutiger World',
          gap: '0.25rem',
          paddingRight: '45px !important',
        },

        '.MuiInputBase-root:has(.MuiChip-root)': {
          fontFamily: 'Neue Frutiger World',
          padding: '0.55rem !important',
          paddingRight: '45px !important',
        },
      },
      popper: {
        '& .MuiPaper-root': {
          padding: 0,
        },
      },
      listbox: {
        padding: '0.75rem',

        '.MuiAutocomplete-option': {
          padding: 0,
          color: palette.grey[800],
          fontSize: '0.75rem',
          fontStyle: 'normal',
          fontWeight: 400,
          lineHeight: '160%',

          '.list-item': {
            padding: '0.25rem',
          },
        },

        '.MuiAutocomplete-option:not(:last-child)': {
          marginBottom: '0.25rem',
        },

        '.MuiAutocomplete-option.Mui-focused': {
          backgroundColor: 'transparent',
        },
        '.MuiAutocomplete-option[aria-selected="true"].Mui-focused': {
          backgroundColor: 'transparent',
        },

        '.MuiAutocomplete-option[aria-selected="true"]': {
          backgroundColor: 'transparent',

          ':hover': {
            backgroundColor: 'transparent',
          },

          '.list-item': {
            flex: 1,
            borderRadius: '0.25rem',
            backgroundColor: palette.accent.light,
          },
        },

        '.MuiCheckbox-root': {
          padding: 0,
        },
      },
    },
  },
  MuiPopover: {
    styleOverrides: {
      root: {
        fontFamily: 'Neue Frutiger World',

        '& .MuiPaper-root': {
          padding: 0,
        },
      },
    },
  },
  MuiPopper: {
    styleOverrides: {
      root: {
        fontFamily: 'Neue Frutiger World',
      },
    },
  },
  // DataGrid's own column-header context menu (Sort/Filter/Hide) is a `Popper`
  // styled via its OWN `MuiDataGrid` "Menu" slot (see
  // `@mui/x-data-grid`'s `GridMenu.js`: `styled(Popper, { name: 'MuiDataGrid',
  // slot: 'Menu' })`), NOT the generic `MuiPopper` component — so this can
  // target just that menu instead of every Popper app-wide (a blanket
  // `MuiPopper` override previously fought the new Calendar* components'
  // Paper padding wherever THEY render inside a Popper — e.g. the Timeline
  // custom-field date-range picker).
  MuiDataGrid: {
    styleOverrides: {
      menu: {
        '& .MuiPaper-root': {
          padding: 0,
          minWidth: 'auto',
        },

        '& .MuiBox-root': {
          padding: '1rem',
        },
      },
    },
  },
  MuiMenu: {
    styleOverrides: {
      root: {
        fontFamily: 'Neue Frutiger World',
        '& .MuiListItemIcon-root': {
          marginRight: '0.5rem',
          minWidth: '1rem !important',
          width: '1rem',

          '& > svg': {
            fill: palette.grey[700],
            width: '1rem',
            height: '1rem',
          },
        },

        '& .MuiListItemText-root': {
          color: palette.grey[700],
          fontSize: '0.75rem',
          fontWeight: 400,
          lineHeight: '160%',
        },
      },
    },
  },
  MuiPickersDay: {
    styleOverrides: {
      root: {
        '&.Mui-selected': {
          backgroundColor: palette.accent.main,
          color: palette.accent.contrastText,
          '&:hover': {
            backgroundColor: palette.accent.dark,
          },
          '&:focus': {
            backgroundColor: palette.accent.main,
          },
        },
        '&.MuiPickersDay-today': {
          borderColor: palette.accent.main,
        },
      },
    },
  },
  // @ts-ignore - MUI X component not in MUI core theme types
  MuiDateRangePickerDay: {
    styleOverrides: {
      rangeIntervalDayHighlight: ({ theme }: { theme: Theme }) => ({
        backgroundColor: theme.palette.accent.light,
        '&:has(.MuiPickersDay-hiddenDaySpacingFiller)': {
          backgroundColor: 'transparent',
        },
      }),
      dayInsideRangeInterval: () => ({
        '&:not(.MuiPickersDay-today)': {
          borderRadius: 0,
        },
      }),
    },
  },
  MuiMultiSectionDigitalClockSection: {
    styleOverrides: {
      item: {
        color: 'black',
        '&.Mui-selected': {
          backgroundColor: palette.accent.main,
          color: 'white',
        },
        '&.Mui-selected:hover': {
          backgroundColor: palette.accent.main,
        },
        '&.Mui-selected:focus': {
          backgroundColor: palette.accent.main,
        },
      },
    },
  },
});
